import React, { useEffect, useRef, useState } from 'react';

import { format } from 'date-fns';

import MetricCell from './MetricCell';
import ShapeCluster from './ShapeCluster';

import { downloadFileFromUrl } from 'Helpers/urlService';

import Tooltip from 'CommonComponents/Tooltip';

import CircleDoneIcon from 'Assets/icons/circle-check.svg';
import CircleCancelIcon from 'Assets/icons/circle-xmark.svg';
import CircleXMarkIcon from 'Assets/icons/circle-xmark.svg';
import DotsHorizontalIcon from 'Assets/icons/dots-horizontal.svg';

/**
 * The "⋯" kebab for a version row: opens a small menu with a single
 * "Edit snapshot…" action (the rename flow) plus a hint line. Closes on
 * outside-click and Escape, and is keyboard/focus accessible. (IB-757 v2 polish)
 */
function VersionActionsMenu({ onEdit }) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const onDocumentClick = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', onDocumentClick);
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('mousedown', onDocumentClick);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    return (
        <div className="tw-relative" ref={containerRef}>
            <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={$t('Version actions')}
                onClick={() => setOpen((current) => !current)}
                className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded tw-border tw-border-grey tw-text-charcoal-75 hover:tw-border-charcoal-50"
            >
                <DotsHorizontalIcon className="tw-h-4 tw-w-4" />
            </button>
            {open && (
                <div
                    role="menu"
                    className="tw-absolute tw-end-0 tw-top-9 tw-z-60 tw-min-w-[192px] tw-rounded-lg tw-border tw-border-grey tw-bg-white tw-p-1.5 tw-text-start tw-shadow-primary"
                >
                    <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                            setOpen(false);
                            onEdit();
                        }}
                        className="tw-flex tw-w-full tw-items-center tw-gap-x-2 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-charcoal hover:tw-bg-background"
                    >
                        <span className="tw-text-amber-700">✎</span> {$t('Edit snapshot…')}
                    </button>
                    <div className="tw-px-3 tw-py-1 tw-text-2xs tw-leading-snug tw-text-charcoal-50">
                        {$t('Editing changes a frozen version and is recorded in the history.')}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function BudgetsTableRow({ budget, downloadUrl, comparison, ...props }) {
    const [isLoading, setIsLoading] = useState(false);
    const [versionName, setVersionName] = useState(budget.name);

    // Shape-of-budget deltas re-base with the Compare dropdown, exactly like the
    // money movements. `comparison` is now the dual { prior, base } object: use
    // the selected-base diff when a base is chosen, otherwise the vs-prior diff.
    // Either can be a sentinel string (the reference / earliest row), in which
    // case there is no delta to show. (IB-757)
    const activeComparison =
        comparison && typeof comparison === 'object'
            ? comparison.base && typeof comparison.base === 'object'
                ? comparison.base
                : typeof comparison.prior === 'object'
                  ? comparison.prior
                  : null
            : null;
    const shapeDeltas = activeComparison || {};

    // In-version read-only context (IB-756): a locked version viewed read-only
    // presents the versions list as a read-only "Budget" tab, so suppress every
    // edit affordance — the rename action, attachment delete, and the Edit menu.
    // Set globally from budget_read_only() in layouts/app.blade.php.
    const readOnly = typeof window !== 'undefined' && !!window.budgetReadOnly;
    const canEdit = props.isEditable && !readOnly;

    const changeVersionName = (e) => {
        setVersionName(e.target.value);
    };

    const cancelEdit = () => {
        setVersionName(budget.name);
        props.cancelBudgetSelected();
    };

    const saveBudgetVersion = () => {
        if (!isLoading) {
            setIsLoading(true);
            axios
                .put(props.updateUrl.replace('budget_id', budget.id), { name: versionName })
                .then((res) => {
                    setIsLoading(false);
                    props.cancelBudgetSelected();
                    props.fetchBudgets();
                })
                .catch((e) => {
                    setIsLoading(false);
                    console.log(e);
                });
        }
    };

    const getDownloadUrl = (budgetId, attachmentId) => {
        return downloadUrl.replace('budget_id', budgetId).replace('attachment_id', attachmentId);
    };

    const getDeleteAttachmentUrl = (budgetId, attachmentId) => {
        return props.deleteAttachmentUrl.replace('budget_id', budgetId).replace('attachment_id', attachmentId);
    };

    const downloadAttachment = (e, budgetId, attachmentId, name) => {
        e.preventDefault();
        downloadFileFromUrl(getDownloadUrl(budgetId, attachmentId));
    };

    const deleteAttachment = (e, budgetId, attachmentId) => {
        e.preventDefault();
        if (confirm($t('Are you sure you want to remove the attachment?'))) {
            if (!isLoading) {
                setIsLoading(true);
                axios
                    .delete(getDeleteAttachmentUrl(budgetId, attachmentId))
                    .then((res) => {
                        setIsLoading(false);
                        props.fetchBudgets();
                    })
                    .catch((e) => {
                        setIsLoading(false);
                        console.log(e);
                    });
            }
        }
    };

    return (
        <tr key={budget.id}>
            {props.compareMode && (
                <td className="tw-w-10 tw-px-2 tw-text-center tw-align-middle">
                    <input
                        type="checkbox"
                        aria-label={$t('Select version for comparison')}
                        checked={!!props.isCompareSelected}
                        onChange={() => props.onToggleCompareSelection(budget.id)}
                    />
                </td>
            )}
            <td className="tw-align-middle">
                {budget.isSelected ? (
                    <div className="tw-flex tw-items-center tw-gap-x-2">
                        <input
                            className="text-input"
                            type="text"
                            required
                            value={versionName}
                            onChange={(e) => {
                                changeVersionName(e);
                            }}
                            autoFocus
                            disabled={isLoading}
                        />
                        <button disabled={isLoading} onClick={() => saveBudgetVersion()}>
                            <CircleDoneIcon className="tw-h-5 tw-w-5 tw-text-green-500 tw-cursor-pointer" />
                        </button>
                        <button disabled={isLoading} onClick={() => cancelEdit()}>
                            <CircleCancelIcon className="tw-h-5 tw-w-5 tw-text-red-500 tw-cursor-pointer" />
                        </button>
                    </div>
                ) : (
                    <div className="tw-flex tw-flex-col tw-gap-y-1">
                        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
                            {canEdit ? (
                                <Tooltip placement="bottom" tooltipContent={$t('Click to rename')} tooltipType="text">
                                    <button
                                        type="button"
                                        onClick={() => props.selectBudget(budget.id)}
                                        className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-start tw-font-semibold tw-text-blue hover:tw-underline"
                                    >
                                        {budget.name} {$t('(v{{version}})', { version: budget.version })}
                                    </button>
                                </Tooltip>
                            ) : (
                                <span className="tw-font-semibold tw-text-charcoal">
                                    {budget.name} {$t('(v{{version}})', { version: budget.version })}
                                </span>
                            )}
                            {budget.edited_count > 0 && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        props.onOpenHistory(budget);
                                    }}
                                    className="tw-inline-flex tw-items-center tw-gap-x-1 tw-rounded-full tw-border tw-border-amber-300 tw-bg-amber-100 tw-px-2 tw-py-0.5 tw-text-2xs tw-font-semibold tw-text-amber-700 hover:tw-bg-amber-200"
                                >
                                    <span aria-hidden="true">⚠</span>
                                    {budget.edited_count > 1 ? $t('Edited ×{{count}}', { count: budget.edited_count }) : $t('Edited')}
                                </button>
                            )}
                        </div>
                        {budget.rationale && <div className="tw-max-w-xs tw-text-xs tw-text-charcoal-50">{budget.rationale}</div>}
                        {budget.attachments.length > 0 && (
                            <ul className="tw-mt-1 tw-flex tw-flex-col tw-gap-y-0.5">
                                {budget.attachments.map((attachment) => (
                                    <li key={attachment.id} className="tw-flex tw-items-center tw-gap-x-1.5 tw-text-xs">
                                        <a
                                            href=""
                                            onClick={(e) => {
                                                downloadAttachment(e, budget.id, attachment.id, attachment.name);
                                            }}
                                        >
                                            {attachment.name}
                                        </a>
                                        {!readOnly && (
                                            <button
                                                disabled={isLoading}
                                                onClick={(e) => {
                                                    deleteAttachment(e, budget.id, attachment.id);
                                                }}
                                            >
                                                <CircleXMarkIcon className="tw-w-3.5 tw-h-3.5 tw-text-red-500 tw-cursor-pointer" />
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </td>

            <MetricCell metric="revenue" metrics={budget.metrics} comparison={comparison} />
            <MetricCell metric="expenditure" metrics={budget.metrics} comparison={comparison} />
            <MetricCell metric="net" metrics={budget.metrics} comparison={comparison} />
            <ShapeCluster metrics={budget.metrics} deltas={shapeDeltas} />

            <td className="inbud-date-cell tw-text-center tw-align-middle">{format(new Date(budget.created_at), 'dd-MM-yyyy')}</td>
            <td className="tw-align-middle">
                <div className="tw-flex tw-items-center tw-justify-center tw-gap-x-2">
                    {budget.switch_url && (
                        <a href={budget.switch_url} className="tw-font-medium tw-text-blue">
                            {$t('View')}
                        </a>
                    )}
                    {canEdit && <VersionActionsMenu onEdit={() => props.onEditSnapshot(budget)} />}
                </div>
            </td>
        </tr>
    );
}
